import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import { type ApiTraining } from '@agunity/api-v4';

import AgLoadingBackdrop from '../AgLoadingBackdrop';
import AgTextField from '../../stories/Components/AgTextField';
import AgDatePicker from '../../stories/Components/CustomDropdowns/AgDatePicker';

import { getTrainingItem } from './redux';
import { useAppDispatch } from '../../redux/hooks';

type ContentModal = {
  topicName?: string;
  canEdit?: boolean;
  trainingId: number;
  userId: number;
  isAdd?: boolean;
  closeModal: () => void;
  callback: () => void;
}

const format = 'YYYY-MM-DD';

const ContentModal = ({ topicName, trainingId, userId }: ContentModal) => {
  const messages = useIntl().formatMessage;

  const dispatch = useAppDispatch();

  const [details, setDetails] = useState<ApiTraining>({});
  const [isLoading, setIsLoading] = useState(false);

  // Training item details
  const getTraining = useCallback(async () => {
    if(!trainingId) return;
    setIsLoading(true);
    try {
      const { data } = await dispatch(getTrainingItem(userId, trainingId!));
      setDetails(data);
    } catch(error){
      console.error(error);
    } finally {
    setIsLoading(false);
    }
  }, [trainingId, userId, dispatch]);

  useEffect(() => {
    const debounce = setTimeout(getTraining);
    return () => clearTimeout(debounce);
  }, [getTraining]);

  return (
    <>
      {isLoading ? (
        <AgLoadingBackdrop />
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AgTextField
            disabled
            value={topicName || ''}
            label={messages({ id: 'TRAINING_TOPIC_NAME' })}
          />
        </Grid>
  
        <Grid item xs={12} md={6}>
          <AgDatePicker
            id='session-start-date'
            disabled
            label={messages({ id: 'TRAINING_START_SESSION_DATE' })}
            value={details.startSessionDate ? dayjs(details.startSessionDate, format) : undefined}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <AgDatePicker
              timeOnly
              id='session-start-time'
              disabled
              label={messages({ id: 'TRAINING_START_SESSION_TIME' })}
              value={details.startSessionDate ? dayjs(details.startSessionDate) : undefined}
            />
        </Grid>
  
        <Grid item xs={12} md={6}>
          <AgDatePicker
            id='session-end-date'
            disabled
            label={messages({ id: 'TRAINING_END_SESSION_DATE' })}
            value={details.endSessionDate ? dayjs(details.endSessionDate, format) : undefined}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <AgDatePicker
              timeOnly
              id='session-end-time'
              disabled
              label={messages({ id: 'TRAINING_END_SESSION_TIME' })}
              value={details.endSessionDate ? dayjs(details.endSessionDate) : undefined}
            />
        </Grid>
        
        <Grid item xs={12}>
          <AgTextField
            disabled
            value={details.location || ''}
            label={messages({ id: 'TRAINING_LOCATION' })}
          />
        </Grid>
        
        <Grid item xs={12}>
          <AgTextField
            disabled
            value={details?.facilitators || ''}
            label={messages({ id: 'TRAINING_FACILITATORS' })}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ContentModal;